import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, BookOpenText, Users, CalendarClock, Library, LogOut, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Дэшборд", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/pnl", label: "P&L", icon: BookOpenText, testid: "nav-pnl" },
  { to: "/clients", label: "Клиенты", icon: Users, testid: "nav-clients" },
  { to: "/upcoming", label: "Предстоящие выплаты", icon: CalendarClock, testid: "nav-upcoming" },
  { to: "/dictionaries", label: "Справочники", icon: Library, testid: "nav-dictionaries" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.map(({ to, label, icon: Icon, testid }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          data-testid={testid}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `nav-link flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm ${
              isActive ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
            }`
          }
        >
          {({ isActive }) => (
            <span data-active={isActive} className="nav-link flex w-full items-center gap-3">
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{label}</span>
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="app-shell relative min-h-screen">
      <div className="relative z-10 flex min-h-screen">
        <aside className="glass fixed inset-y-0 left-0 hidden w-64 flex-col border-r lg:flex">
          <div className="flex h-16 items-center gap-2 px-6">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary font-display text-sm font-bold text-primary-foreground">
              M
            </div>
            <div>
              <div className="font-display text-base font-bold leading-none">MAYO</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Finance</div>
            </div>
          </div>
          <div className="mt-4 flex-1">{nav}</div>
          <div className="border-t p-4">
            <div className="mb-3 truncate text-xs text-muted-foreground" data-testid="sidebar-user-email">
              {user?.email}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start gap-2"
              data-testid="logout-button"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
            >
              <LogOut className="h-4 w-4" /> Выйти
            </Button>
          </div>
        </aside>

        <div className="flex w-full flex-1 flex-col lg:pl-64">
          <header className="glass sticky top-0 z-20 flex h-16 items-center justify-between border-b px-4 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary font-display text-sm font-bold text-primary-foreground">
                M
              </div>
              <span className="font-display font-bold">MAYO Finance</span>
            </div>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </header>
          {open && (
            <div className="glass border-b py-3 lg:hidden" data-testid="mobile-nav">
              {nav}
              <div className="px-3 pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2"
                  data-testid="logout-button-mobile"
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                >
                  <LogOut className="h-4 w-4" /> Выйти
                </Button>
              </div>
            </div>
          )}
          <main className="flex-1 px-4 py-6 sm:px-6 lg:px-10 lg:py-8">{children}</main>
        </div>
      </div>
    </div>
  );
}
